import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { PageHeader, PageHeaderWithBack } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { sitesService } from "@/api/services/sites.service";
import { SiteDetailsTab, SiteDocumentsPhotosTab } from "./components";
import styles from "./SiteCreate.module.css";
const TABS = [
    { id: "details", label: "Site Details" },
    { id: "documents", label: "Documents and Photos" },
];
export const SiteCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("details");
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creatingEstimate, setCreatingEstimate] = useState(false);
    const [error, setError] = useState(null);
    const [isSummaryTabActive, setIsSummaryTabActive] = useState(false);
    // Determine base path (admin or staff)
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    // Ref to hold the save function from SiteDetailsTab
    const saveHandlerRef = useRef(null);
    // Ref to hold the create estimate function from SiteDetailsTab
    const createEstimateHandlerRef = useRef(null);
    const createdSiteIdRef = useRef(null);
    const loadDraftSite = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await sitesService.getDefaultWorkItems();
            const timestamp = new Date().toISOString();
            const draftSite = {
                id: "",
                siteName: "",
                tenancyName: "",
                tenancyNumber: "",
                address: "",
                suburb: "",
                state: "",
                postcode: "",
                buildingTypes: [],
                landlord: "",
                contactPerson: null,
                companyName: null,
                jobNumber: null,
                description: null,
                notes: null,
                createdAt: timestamp,
                updatedAt: timestamp,
                workItems: response.data,
                versions: [],
            };
            setSite(draftSite);
        }
        catch (err) {
            setError("Failed to load site template");
            console.error("Error loading site template:", err);
            setSite(null);
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        loadDraftSite();
    }, [loadDraftSite]);
    const handleSiteCreated = useCallback((siteId) => {
        createdSiteIdRef.current = siteId;
    }, []);
    const handleSaveClick = async () => {
        if (saveHandlerRef.current) {
            setSaving(true);
            try {
                const savedSiteId = await saveHandlerRef.current();
                const siteIdToRefresh = savedSiteId || createdSiteIdRef.current || site?.id;
                if (siteIdToRefresh) {
                    const response = await sitesService.getSite(siteIdToRefresh);
                    setSite(response.data);
                }
                // Check if returnUrl is provided
                const returnUrl = searchParams.get("returnUrl");
                if (returnUrl && savedSiteId) {
                    // Redirect back to the returnUrl with the siteId parameter
                    navigate(`${returnUrl}?siteId=${savedSiteId}`);
                }
                else {
                    // Redirect to sites list after successful save
                    navigate(`${basePath}/sites`);
                }
            }
            catch (err) {
                console.error("Error saving:", err);
            }
            finally {
                setSaving(false);
            }
        }
    };
    const handleCreateEstimateClick = async () => {
        if (createEstimateHandlerRef.current) {
            setCreatingEstimate(true);
            try {
                await createEstimateHandlerRef.current();
            }
            catch (err) {
                console.error("Error creating estimate:", err);
            }
            finally {
                setCreatingEstimate(false);
            }
        }
    };
    const handleSummaryTabChange = useCallback((isSummary) => {
        setIsSummaryTabActive(isSummary);
    }, []);
    const handleBackClick = () => {
        navigate(-1);
    };
    const renderTabContent = () => {
        if (loading) {
            return (_jsx("div", { className: styles.loading, children: "Loading site template..." }));
        }
        if (error) {
            return _jsx("div", { className: styles.error, children: error });
        }
        if (!site) {
            return (_jsx("div", { className: styles.error, children: "Failed to load site template" }));
        }
        switch (activeTab) {
            case "details":
                return (_jsx(SiteDetailsTab, { site: site, saveHandlerRef: saveHandlerRef, createEstimateHandlerRef: createEstimateHandlerRef, onSummaryTabChange: handleSummaryTabChange, onSiteCreated: handleSiteCreated }));
            case "documents":
                return _jsx(SiteDocumentsPhotosTab, {});
            default:
                return null;
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Add New Site", onBack: handleBackClick, breadcrumb: "Sites/Add New Site" }), _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, role: "tablist", children: TABS.map((tab) => (_jsx("button", { className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => setActiveTab(tab.id), role: "tab", "aria-selected": activeTab === tab.id, children: tab.label }, tab.id))) }) }), _jsxs("div", { className: styles.contentArea, children: [renderTabContent(), activeTab === "details" && !loading && site && (_jsxs("div", { className: styles.summaryActions, children: [_jsx("button", { className: styles.saveButton, onClick: handleSaveClick, disabled: saving || loading || !site, children: saving ? "Saving..." : "Save" }), isSummaryTabActive && (_jsx("button", { className: styles.createEstimateButton, onClick: handleCreateEstimateClick, disabled: creatingEstimate, children: creatingEstimate ? "Creating..." : "Create Estimate" }))] }))] })] }));
};
